import { __assign } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Stack, Typography, LinearProgress, Skeleton, Button, } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import find from "lodash/find";
import React, { useEffect, useRef, useState } from "react";
import { useStoreFileViewerContext } from "../../hooks/context";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { uploadFiles } from "../UploadPopover/hooks";
import TargetPopper from "./TargetPopper";
var FileDropBox = function (_a) {
    var _b, _c;
    var children = _a.children, BoxSx = _a.BoxSx;
    var theme = useTheme();
    var _d = useStoreFileViewerContext(), parent = _d.parent, data = _d.data, loading = _d.loading;
    var objects = useRef((_b = data === null || data === void 0 ? void 0 : data.objects) !== null && _b !== void 0 ? _b : []);
    objects.current = (_c = data === null || data === void 0 ? void 0 : data.objects) !== null && _c !== void 0 ? _c : [];
    var _e = useState(parent), targetParent = _e[0], setTargetParent = _e[1];
    var parentRef = useRef(targetParent);
    parentRef.current = targetParent;
    useEffect(function () {
        setTargetParent(parent);
    }, [parent]);
    var drop = useRef(null);
    var exit = useRef(null);
    var isMissingData = !loading && !parent && !data.objects.length;
    var inputRef = useRef();
    var _f = useState(false), dragging = _f[0], setDragging = _f[1];
    React.useEffect(function () {
        var _a, _b, _c, _d;
        (_a = drop.current) === null || _a === void 0 ? void 0 : _a.addEventListener("dragover", handleDragOver);
        (_b = drop.current) === null || _b === void 0 ? void 0 : _b.addEventListener("drop", handleDrop);
        (_c = drop.current) === null || _c === void 0 ? void 0 : _c.addEventListener("dragenter", handleDragEnter);
        (_d = drop.current) === null || _d === void 0 ? void 0 : _d.addEventListener("dragleave", handleDragLeave);
        return function () {
            var _a, _b, _c, _d;
            (_a = drop.current) === null || _a === void 0 ? void 0 : _a.removeEventListener("dragover", handleDragOver);
            (_b = drop.current) === null || _b === void 0 ? void 0 : _b.removeEventListener("drop", handleDrop);
            (_c = drop.current) === null || _c === void 0 ? void 0 : _c.removeEventListener("dragenter", handleDragEnter);
            (_d = drop.current) === null || _d === void 0 ? void 0 : _d.removeEventListener("dragleave", handleDragLeave);
        };
    }, []);
    var updateTarget = function (element) {
        if (element.getAttribute("data-field") === "name") {
            var row = element.parentElement;
            if (row) {
                var objectId_1 = row.getAttribute("data-id");
                if (objectId_1) {
                    var object = find(objects.current, function (o) { return o.id === objectId_1; });
                    if (object && object.type === "folder") {
                        setTargetParent(object);
                        return true;
                    }
                }
            }
        }
        return false;
    };
    var handleDragOver = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (exit.current) {
            clearTimeout(exit.current);
            exit.current = null;
        }
        setDragging(true);
        if (e.target.getAttribute("data-field") === "name") {
            if (!updateTarget(e.target)) {
                setTargetParent(parent);
            }
        }
        else {
            var cell = e.target.closest(".MuiDataGrid-Cell");
            if (cell && updateTarget(cell)) {
                // Do nothing, parent is targetted
            }
            else {
                setTargetParent(parent);
            }
        }
    };
    var handleDrop = function (e) {
        e.preventDefault();
        e.stopPropagation();
        var files = e.dataTransfer.files;
        if (files && files.length) {
            uploadFiles(files, parentRef.current);
        }
        setDragging(false);
    };
    var handleDragEnter = function (e) {
        e.preventDefault();
        e.stopPropagation();
    };
    var handleDragLeave = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!exit.current) {
            exit.current = setTimeout(function () {
                setDragging(false);
            }, 200);
        }
    };
    var main = theme.palette.info.main;
    return (_jsxs(_Fragment, { children: [_jsx("input", { 
                // @ts-ignore
                ref: inputRef, style: { display: "none" }, type: "file", onChange: function (e) {
                    if (e.target.files) {
                        uploadFiles(e.target.files);
                    }
                } }), _jsxs(Stack, { ref: drop, direction: "row", sx: __assign(__assign({ position: "relative", width: "100%", border: "2px solid transparent" }, (!!dragging && {
                    cursor: "pointer",
                    border: "2px solid ".concat(main),
                })), BoxSx), children: [isMissingData ? (_jsxs(Stack, { spacing: -1, width: "100%", height: "100%", overflow: "hidden", children: [_jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Skeleton, { height: "50px" }), _jsx(Box, { sx: {
                                    position: "absolute",
                                    top: "calc(50% - 40px)",
                                    bottom: "16px",
                                    left: "calc(50% - 100px)",
                                }, children: _jsxs(Stack, { spacing: 2, sx: { maxWidth: "210px", textAlign: "center" }, alignItems: "center", children: [_jsxs(Typography, { variant: "body2", children: [_jsx("span", { style: { fontWeight: "bold" }, children: "Drag and drop" }), " ", "files or click the button to upload"] }), _jsx(Button, { startIcon: _jsx(AddIcon, { fontSize: "small" }), size: "small", variant: "contained", onClick: function (e) {
                                                var _a;
                                                e.stopPropagation();
                                                e.preventDefault();
                                                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
                                            }, children: "Upload" })] }) })] })) : (_jsx(_Fragment, { children: children !== null && children !== void 0 ? children : (_jsxs(Stack, { gap: 1, width: "100%", height: "100%", paddingY: 6, alignItems: "center", justifyContent: "center", children: [_jsx(FileUploadOutlinedIcon, { sx: { fontSize: "40px" } }), !loading && dragging && (_jsx(Typography, { variant: "body2", children: "Drop files to upload" })), !loading && !dragging && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", children: "Drag files here to upload" }), _jsx(Typography, { variant: "body2", sx: { color: theme.palette.info.main }, children: "or choose a file" })] })), loading && (_jsx(LinearProgress, { color: "info", sx: { height: "2px", width: "100%", maxWidth: "200px" } }))] })) })), _jsx(TargetPopper, { open: dragging, parent: targetParent })] })] }));
};
export default FileDropBox;
